import {useQuery} from '@tanstack/react-query'

import {SlotValidationResult, UseSlotValidationParams} from '../../../../slotManagement'
import {useSlotManagementContext} from '../../../../slotManagement/context/useSlotManagementContext'
import {composeSlotValidationCallbacks} from '../../../../slotManagement/utils/composeSlotValidationCallbacks'

import {useOrderChangeSlotValidationEnabled} from './useOrderChangeSlotValidationEnabled'

/**
 * Order Change slot validation hook.
 * Validates slot for a single dateTime when needed.
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber, orderId).
 *
 * Validation logic:
 * 1. Skip if order is auto-confirmable (no dispatch approval needed)
 * 2. Skip if no slot-managed fields are dirty
 * 3. Otherwise validate the slot
 *
 * Validation is disabled when:
 * - Feature toggle is off
 * - Order is auto-confirmable
 * - No slot-managed fields are dirty
 * - Slot request params cannot be built (validation failed)
 * - No query function provided
 *
 * Side effect: Updates slot availability state in context based on validation result.
 * Consumer callbacks (onSuccess/onError) are preserved and composed with internal state updates.
 */
export const useOrderChangeSlotValidation = ({
  slotValidationQueryFn
}: UseSlotValidationParams): SlotValidationResult[] => {
  const {slotAvailabilities, setSlotAvailability} = useSlotManagementContext()

  // Determine if validation should be enabled
  const {isEnabled, slotRequestResult} = useOrderChangeSlotValidationEnabled()

  // Determine if we should validate
  const shouldValidate = isEnabled && !!slotValidationQueryFn

  // Get query options and add callbacks
  const baseOptions =
    shouldValidate && slotRequestResult.params
      ? slotValidationQueryFn(slotRequestResult.params)
      : null

  const queryOptions = baseOptions
    ? composeSlotValidationCallbacks(baseOptions, 0, setSlotAvailability)
    : {
        queryKey: ['slot-validation-disabled'],
        queryFn: () => ({valid: false}),
        enabled: false
      }

  const query = useQuery(queryOptions)

  return [
    {
      query,
      ...slotAvailabilities[0]
    }
  ]
}
